import { useEffect, useMemo, useState } from 'react'
import {
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { Card, Button, Title, Text, Icon } from '@nimbus-ds/components'
import { StatsIcon } from '@nimbus-ds/icons'
import { funnelService, type ProductsOriginsTimelineResponse, type ProductsOriginsTimelinePoint } from '@/api/funnel'

type Metric = 'sessions' | 'conversions' | 'conversion_rate'
type Smoothing = 'raw' | 'ma3'
type Dimension = 'channel' | 'origin' | 'campaign' | 'device'

const METRIC_LABELS: Record<Metric, string> = {
  sessions: 'Sesiones',
  conversions: 'Conversiones',
  conversion_rate: '% Conv.',
}

const SMOOTHING_LABELS: Record<Smoothing, string> = {
  raw: 'Diario',
  ma3: '3d avg',
}

const DIMENSION_LABELS: Record<Dimension, string> = {
  channel: 'Grupo',
  origin: 'Origen',
  campaign: 'Campaña',
  device: 'Dispositivo',
}

interface TrafficSourceTimelineProps {
  storeId: string
  windowDays?: number
  initialData?: ProductsOriginsTimelineResponse | null
}

interface SeriesDef { key: string; label: string; color: string }

// GA4-style channels (keys match the pipeline's categorizeChannel + GA4 names).
// 'unattributed' is intentionally excluded — it's not a real channel.
const CHANNEL_DEFS: SeriesDef[] = [
  { key: 'Paid Search', label: 'Búsqueda pagada', color: '#1e3a8a' },
  { key: 'Paid Social', label: 'Social pagado', color: '#8b5cf6' },
  { key: 'Organic Search', label: 'Búsqueda orgánica', color: '#3b82f6' },
  { key: 'Organic Social', label: 'Social orgánico', color: '#ec4899' },
  { key: 'Email', label: 'Email', color: '#0d9488' },
  { key: 'Referral', label: 'Referidos', color: '#94a3b8' },
  { key: 'Paid Other', label: 'Otros pagados', color: '#dc2626' },
  { key: 'Direct', label: 'Directo', color: '#10b981' },
]

// Source-bucket view (the 9 origin buckets), one finer than channels.
const ORIGIN_DEFS: SeriesDef[] = [
  { key: 'google', label: 'Google (Orgánico)', color: '#3b82f6' },
  { key: 'google_paid', label: 'Google Ads', color: '#1e3a8a' },
  { key: 'facebook', label: 'Facebook', color: '#1d4ed8' },
  { key: 'instagram', label: 'Instagram', color: '#ec4899' },
  { key: 'meta_ads', label: 'Meta Ads', color: '#8b5cf6' },
  { key: 'direct', label: 'Directo', color: '#10b981' },
  { key: 'other_search', label: 'Otros buscadores', color: '#f59e0b' },
  { key: 'other_paid', label: 'Otros pagados', color: '#dc2626' },
  { key: 'other_external', label: 'Otros referidos', color: '#94a3b8' },
]

// Device dimension (mobile/desktop/unknown). `unknown` = no viewport seen (measurement gap).
const DEVICE_DEFS: SeriesDef[] = [
  { key: 'mobile', label: 'Celular', color: '#3b82f6' },
  { key: 'desktop', label: 'Escritorio', color: '#8b5cf6' },
  { key: 'unknown', label: 'Sin dato', color: '#94a3b8' },
]

const CAMPAIGN_COLORS = ['#8b5cf6', '#3b82f6', '#ec4899', '#10b981', '#f59e0b', '#dc2626', '#0d9488', '#6366f1']

export function TrafficSourceTimeline({
  storeId,
  windowDays = 7,
  initialData,
}: TrafficSourceTimelineProps) {
  const [data, setData] = useState<ProductsOriginsTimelineResponse | null>(initialData ?? null)
  const [isLoading, setIsLoading] = useState(!initialData)
  const [error, setError] = useState<string | null>(null)
  const [metric, setMetric] = useState<Metric>('sessions')
  const [smoothing, setSmoothing] = useState<Smoothing>('raw')
  const [dimension, setDimension] = useState<Dimension>('channel')
  const [hidden, setHidden] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (initialData) {
      setData(initialData)
      setIsLoading(false)
      return
    }
    if (!storeId) return
    let cancelled = false
    setIsLoading(true)
    setError(null)
    funnelService
      .getStoreProductsOriginsTimeline(storeId, windowDays)
      .then((res) => {
        if (cancelled) return
        setData(res)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load traffic source timeline:', err)
        setError('No se pudo cargar la evolución de orígenes')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [storeId, windowDays, initialData])

  const { chartData, visibleSources } = useMemo(() => {
    const empty = { chartData: [] as Record<string, string | number | null>[], visibleSources: [] as SeriesDef[] }
    if (!data) return empty

    // Active series defs + a per-point {sessions, conversions} accessor, by dimension.
    let defs: SeriesDef[]
    let valueAt: (point: ProductsOriginsTimelinePoint, key: string) => { sessions: number; conversions: number }

    if (dimension === 'origin') {
      defs = ORIGIN_DEFS
      valueAt = (p, k) => {
        const c = (p.origins as Record<string, { sessions: number; conversions: number }> | undefined)?.[k]
        return { sessions: c?.sessions ?? 0, conversions: c?.conversions ?? 0 }
      }
    } else if (dimension === 'device') {
      defs = DEVICE_DEFS
      valueAt = (p, k) => {
        const c = (p.devices as Record<string, { sessions: number; conversions: number }> | undefined)?.[k]
        return { sessions: c?.sessions ?? 0, conversions: c?.conversions ?? 0 }
      }
    } else if (dimension === 'campaign') {
      // Top campaigns across the window (from the window aggregate), plotted per day.
      const totals = new Map<string, number>()
      for (const cell of Object.values(data.traffic_origins_breakdown || {})) {
        for (const c of cell.campaigns || []) totals.set(c.campaign, (totals.get(c.campaign) || 0) + (c.sessions || 0))
      }
      defs = [...totals.entries()].sort((a, b) => b[1] - a[1]).slice(0, CAMPAIGN_COLORS.length)
        .map(([name], i) => ({ key: name, label: name, color: CAMPAIGN_COLORS[i] }))
      valueAt = (p, k) => {
        let sessions = 0, conversions = 0
        for (const cell of Object.values(p.breakdown || {})) {
          const m = (cell.campaigns || []).find((c) => c.campaign === k)
          if (m) { sessions += m.sessions || 0; conversions += m.conversions || 0 }
        }
        return { sessions, conversions }
      }
    } else {
      defs = CHANNEL_DEFS
      valueAt = (p, k) => {
        const c = p.channels?.[k]
        return { sessions: c?.sessions ?? 0, conversions: c?.conversions ?? 0 }
      }
    }

    type Row = Record<string, string | number | null>
    const points: Row[] = (data.series || []).map((point) => {
      const row: Row = {
        date: point.date,
        label: format(new Date(point.date + 'T00:00:00Z'), 'dd MMM', { locale: es }),
      }
      for (const d of defs) {
        const { sessions, conversions } = valueAt(point, d.key)
        if (metric === 'sessions') row[d.key] = sessions
        else if (metric === 'conversions') row[d.key] = conversions
        else row[d.key] = sessions > 0 ? +((conversions / sessions) * 100).toFixed(2) : null
      }
      return row
    })

    // 3-day moving average
    let series = points
    if (smoothing === 'ma3' && points.length) {
      const window = 3
      series = points.map((_, i) => {
        const out: Row = { date: points[i].date, label: points[i].label }
        for (const d of defs) {
          let sum = 0
          let count = 0
          for (let k = Math.max(0, i - window + 1); k <= i; k++) {
            const v = points[k][d.key]
            if (typeof v === 'number') { sum += v; count += 1 }
          }
          out[d.key] = count === 0
            ? (metric === 'conversion_rate' ? null : 0)
            : (metric === 'conversion_rate' ? +(sum / count).toFixed(2) : Math.round((sum / count) * 100) / 100)
        }
        return out
      })
    }

    // Hide series with no signal across the window to keep the chart legible.
    const totals: Record<string, number> = {}
    for (const d of defs) {
      totals[d.key] = series.reduce((sum, row) => sum + (typeof row[d.key] === 'number' ? (row[d.key] as number) : 0), 0)
    }
    const nonZero = defs.filter((d) => totals[d.key] > 0)

    return { chartData: series, visibleSources: nonZero }
  }, [data, metric, smoothing, dimension])

  const toggleSource = (key: string) => {
    setHidden((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  if (isLoading) {
    return (
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Icon source={<StatsIcon />} />
            <Title as="h3">Evolución por Canal</Title>
          </div>
          <Text>Sesiones diarias por canal de tráfico</Text>
        </Card.Header>
        <Card.Body>
          <div className="h-[280px] w-full animate-pulse rounded-md bg-muted" />
        </Card.Body>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <Card.Header>
          <Title as="h3">Evolución por Canal</Title>
        </Card.Header>
        <Card.Body>
          <p className="text-sm text-red-600">{error}</p>
        </Card.Body>
      </Card>
    )
  }

  const header = (
    <Card.Header>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Icon source={<StatsIcon />} />
            <Title as="h3" className="sm:whitespace-nowrap">Evolución por {DIMENSION_LABELS[dimension]}</Title>
          </div>
          <Text>
            {dimension === 'campaign' ? 'Top campañas' : dimension === 'origin' ? 'Orígenes' : 'Canales'} · últimos {windowDays} días
          </Text>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 rounded-md border p-0.5">
            {(Object.keys(DIMENSION_LABELS) as Dimension[]).map((d) => (
              <Button
                key={d}
                size="small"
                appearance={dimension === d ? 'primary' : 'transparent'}
                onClick={() => { setDimension(d); setHidden(new Set()) }}
              >
                {DIMENSION_LABELS[d]}
              </Button>
            ))}
          </div>
          <div className="flex gap-1 rounded-md border p-0.5">
            {(Object.keys(SMOOTHING_LABELS) as Smoothing[]).map((s) => (
              <Button
                key={s}
                size="small"
                appearance={smoothing === s ? 'primary' : 'transparent'}
                onClick={() => setSmoothing(s)}
              >
                {SMOOTHING_LABELS[s]}
              </Button>
            ))}
          </div>
          <div className="flex gap-1 rounded-md border p-0.5">
            {(Object.keys(METRIC_LABELS) as Metric[]).map((m) => (
              <Button
                key={m}
                size="small"
                appearance={metric === m ? 'primary' : 'transparent'}
                onClick={() => setMetric(m)}
              >
                {METRIC_LABELS[m]}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </Card.Header>
  )

  if (!chartData.length || !visibleSources.length) {
    return (
      <Card>
        {header}
        <Card.Body>
          <Text className="py-8 text-center">
            No hay datos suficientes para esta métrica en el rango.
          </Text>
        </Card.Body>
      </Card>
    )
  }

  return (
    <Card>
      {header}
      <Card.Body>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis
              tick={{ fontSize: 12 }}
              allowDecimals={metric === 'conversion_rate'}
              tickFormatter={(v) => (metric === 'conversion_rate' ? `${v}%` : `${v}`)}
            />
            <Tooltip
              formatter={(value: number, name: string) => {
                const def = visibleSources.find((s) => s.key === name)
                const display = def?.label || name
                const formatted = metric === 'conversion_rate' ? `${value}%` : value.toLocaleString()
                return [formatted, display]
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: 12, cursor: 'pointer' }}
              onClick={(entry) => {
                if (entry?.dataKey) toggleSource(String(entry.dataKey))
              }}
              formatter={(value) => {
                const def = visibleSources.find((s) => s.key === value)
                const label = def?.label || value
                const isHidden = hidden.has(String(value))
                return (
                  <span
                    style={{
                      opacity: isHidden ? 0.4 : 1,
                      textDecoration: isHidden ? 'line-through' : 'none',
                    }}
                  >
                    {label}
                  </span>
                )
              }}
            />
            {visibleSources.map((src) => (
              <Line
                key={src.key}
                type="monotone"
                dataKey={src.key}
                stroke={src.color}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                connectNulls={false}
                hide={hidden.has(src.key)}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Card.Body>
    </Card>
  )
}

export default TrafficSourceTimeline
